import { Table } from "../models/Table";
import { TableField } from "../models/TableField";
import { TableVisibilityFieldTypeValues } from "../models/TableFieldTypeArgs";
import { TableSchema } from "../models/TableSchema";
import { TableSchemaUiFieldGroup } from "../models/TableSchemaUi";
import { TablesService } from "./TablesService";
import { TablesTypeGuardService } from "./TablesTypeGuardService";



export class TablesUiService {

    public static getTableSchemaUiFieldGroups = (tableSchema: TableSchema): Array<TableSchemaUiFieldGroup> | null => {

        if (!TablesTypeGuardService.hasDefinedTableSchemaUi(tableSchema)) return null;
        return tableSchema.ui?.field_groups as Array<TableSchemaUiFieldGroup>;

    }

    public static getValueByPath = (configuration: any, path?: string | string[]): any => {
        if (configuration == null) return undefined;
        if (path === undefined || path === null) return undefined;

        const parts: string[] = Array.isArray(path)
            ? path
            : (String(path).match(/[^.[\]]+/g) || []);

        if (parts.length === 0) return configuration;



        let cur: any = configuration;
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (cur == null) return undefined;

            // wildcard: fan out over every element of the current array and collect results
            if (part === '*') {
                if (!Array.isArray(cur)) return undefined;
                const remainingParts = parts.slice(i + 1);
                return cur.map((item: any) => TablesUiService.getValueByPath(item, remainingParts));
            }

            // treat purely-numeric segments as indices
            if (/^\d+$/.test(part)) {
                const idx = Number(part);
                cur = cur[idx];
            } else {
                cur = cur[part];
            }
        }
        return cur;
    }

    // Attempts to determine field visibility based on field visible configuration and optional IAnalysisConfigVisibilityFieldTypeArgs configuration
    // _currentPath is not supported full in ObjectAnalysisConfigBuilder as we have not implemented pathRefs/method for getting current relative path in that component
    public static isFieldVisibleFromRoot = (field: TableField, rootConfiguration?: Table | null, _currentPath?: string[]) => {

        if (field.visible === false) return false;
        if (rootConfiguration != null) {
            // Root analysisConfig object is AnalysisConfig type and must be flattened for paths to be resolved
            const flattenedRootConfiguration = TablesService.getFlattenedTables([rootConfiguration])[0];

            const visibleConfigs = field?.field_type_args?.visible_configs;
            if (Array.isArray(visibleConfigs)) {

                let visible = false;
                visibleConfigs.forEach((config: { visible_prop_path: string, visible_prop_value: any, visible_value?: TableVisibilityFieldTypeValues, visible_parent?: string }) => {
                    const propPath = config?.visible_prop_path;
                    const expected = config?.visible_prop_value;
                    const expectedValueType = config?.visible_value;
                    const visibleParent = config?.visible_parent;

                    let targetPathParts: string[] = [];

                    // If a visible_parent is provided and we have the current path, try to resolve the parent array index
                    if (visibleParent && Array.isArray(_currentPath)) {
                        let foundIndex = -1;
                        // Find the nearest occurrence of visibleParent that is followed by a numeric index
                        for (let i = _currentPath.length - 1; i >= 0; i--) {
                            if (_currentPath[i] === visibleParent && i + 1 < _currentPath.length && /^\d+$/.test(String(_currentPath[i + 1]))) {
                                foundIndex = i;
                                break;
                            }
                        }
                        if (foundIndex !== -1) {
                            const base = _currentPath.slice(0, foundIndex + 2); // includes parent name and its index
                            const propParts = propPath.includes('.') ? propPath.split('.') : [propPath];
                            targetPathParts = [...base, ...propParts];
                        }
                    }

                    // Fallback to existing resolution when visible_parent not provided or resolution failed
                    if (targetPathParts.length === 0) {
                        targetPathParts = propPath.includes('.')
                            ? propPath.split('.')
                            : (_currentPath
                                ? [..._currentPath.slice(0, -1), propPath]
                                : [propPath]);
                    }

                    const val = TablesUiService.getValueByPath(flattenedRootConfiguration, targetPathParts);
                    const flatVal = Array.isArray(val) ? val.flat(Infinity) : val;
                    const matches = Array.isArray(flatVal)
                        ? flatVal.some((v: any) => this.valueMatchesTableVisibilityFieldTypeValue(v, expectedValueType, expected) || v === expected)
                        : this.valueMatchesTableVisibilityFieldTypeValue(flatVal, expectedValueType, expected) || flatVal === expected;
                    visible = visible || matches;

                })
                return visible;
            }

        }
        return true;
    }

    private static valueMatchesTableVisibilityFieldTypeValue(value: any, visibilityValue?: TableVisibilityFieldTypeValues, expected?: any): boolean {

        switch (visibilityValue) {
            case TableVisibilityFieldTypeValues.Defined:
                return value !== undefined;
            case TableVisibilityFieldTypeValues.Falsey:
                return !value;
            case TableVisibilityFieldTypeValues.Truthy:
                return value;
            case TableVisibilityFieldTypeValues.Contains:
                if (Array.isArray(expected) || typeof expected === 'string') {
                    return expected.includes(value);
                }
                console.warn(`TablesUiService: visible_value 'contains' requires visible_prop_value to be an array or string, got: ${typeof expected}`);
                return false;
            case TableVisibilityFieldTypeValues.DoesNotContain:
                if (Array.isArray(expected) || typeof expected === 'string') {
                    return !expected.includes(value);
                }
                console.warn(`TablesUiService: visible_value 'does_not_contain' requires visible_prop_value to be an array or string, got: ${typeof expected}`);
                return false;
            default:
                return false
        }

    }

}